<script setup>
import DeleteUserAction from "@frontJs/components/actions/DeleteUserAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { onMounted, computed, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string, ref as yupRef } from "yup";

const { t } = useI18n();

const { user, roles, tenants } = defineProps({
  user: {
    required: false,
    type: Object,
  },
  roles: {
    required: true,
    type: Array,
  },
  tenants: {
    required: true,
    type: Array,
  },
});

const { route } = useRoute();

const form = reactive({
  lastname: user ? user.lastname : "",
  firstname: user ? user.firstname : "",
  email: user ? user.email : "",
  changePassword: false,
  password: "",
  passwordConfirmation: "",
  roleIds: user
    ? user.roles.reduce((acc, role) => {
        acc[role.id] = true;
        return acc;
      }, {})
    : {},
  tenantIds: user
    ? user.tenants.reduce((acc, tenant) => {
        acc[tenant.id] = true;
        return acc;
      }, {})
    : {},
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

const superAdminRoleId = computed(() => {
  return roles.find((role) => role.name === "super_admin").id;
});

const hasSuperAdminRole = computed(() => {
  return Object.keys(form.roleIds).some((key) => {
    return form.roleIds[key] && Number(key) === Number(superAdminRoleId.value);
  });
});

const validationSchema = object({
  firstname: string().required(t("error.required")),
  lastname: string().required(t("error.required")),
  email: string().required(t("error.required")).email(t("error.email")),
  password: string().when("changePassword", {
    is: true,
    then: () => string().required(t("error.required")),
  }),
  passwordConfirmation: string().when("changePassword", {
    is: true,
    then: () =>
      string()
        .required(t("error.required"))
        .oneOf([yupRef("password")], t("error.passwordConfirmation")),
  }),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveUser() {
  fetch(
    user
      ? route("front.postUser", {
          id: user.id,
        })
      : route("front.postUser"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        email: form.email,
        lastname: form.lastname,
        firstname: form.firstname,
        password: form.changePassword || user == null ? form.password : null,
        passwordConfirmation:
          form.changePassword || user == null
            ? form.passwordConfirmation
            : null,
        roleIds: Object.keys(form.roleIds)
          .filter((key) => form.roleIds[key])
          .map((key) => Number(key)),
        tenantIds: Object.keys(form.tenantIds).filter(
          (key) => form.tenantIds[key],
        ),
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then(() => {
      toast.success(t("toast.successfullySaved"), {
        position: "top-right",
      });

      goBack();
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.users"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  goBack();
}

watch(
  () => form.roleIds[superAdminRoleId.value],
  () => {
    if (hasSuperAdminRole.value) {
      roles.forEach((role) => {
        form.roleIds[role.id] = true;
      });

      tenants.forEach((tenant) => {
        form.tenantIds[tenant.id] = true;
      });
    } else {
      roles.forEach((role) => {
        if (role.id !== superAdminRoleId.value) {
          form.roleIds[role.id] = false;
        }
      });

      tenants.forEach((tenant) => {
        form.tenantIds[tenant.id] = false;
      });
    }
  },
);

onMounted(() => {
  if (hasSuperAdminRole.value) {
    roles.forEach((role) => {
      form.roleIds[role.id] = true;
    });

    tenants.forEach((tenant) => {
      form.tenantIds[tenant.id] = true;
    });
  }
});
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="saveUser"
    v-slot="{ errors }"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
        roles: t('title.roles'),
        customers: t('title.customers'),
      }"
      :replace="false"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <InputField
              v-model="form.firstname"
              :label="t('label.firstname')"
              :placeholder="t('label.firstname')"
              field-name="firstname"
              field-id="firstname"
              type="text"
              required
            />

            <InputField
              v-model="form.lastname"
              :label="t('label.lastname')"
              :placeholder="t('label.lastname')"
              field-name="lastname"
              field-id="lastname"
              type="text"
              required
            />
          </div>

          <InputField
            v-model="form.email"
            :label="t('label.email')"
            :placeholder="t('label.email')"
            field-name="email"
            field-id="email"
            type="email"
            required
          />

          <SwitchField
            v-if="user"
            v-model="form.changePassword"
            :label="t('label.changePassword')"
            field-id="changePassword"
            field-name="changePassword"
          />

          <div
            v-if="form.changePassword || user == null"
            class="grid grid-cols-1 gap-x-8 md:grid-cols-2"
          >
            <InputField
              v-model="form.password"
              :label="t('label.password')"
              :placeholder="t('label.password')"
              field-name="password"
              field-id="password"
              type="password"
              required
            />

            <InputField
              v-model="form.passwordConfirmation"
              :label="t('label.passwordConfirmation')"
              :placeholder="t('label.passwordConfirmation')"
              field-name="passwordConfirmation"
              field-id="passwordConfirmation"
              type="password"
              required
            />
          </div>
        </TabPanel>

        <TabPanel>
          <ul class="flex flex-col items-start">
            <li v-for="(role, index) in roles" :key="index">
              <SwitchField
                v-model="form.roleIds[role.id]"
                :label="t('role.' + role.name)"
                :field-id="'roleIds[' + role.id + ']'"
                :field-name="'roleIds[' + role.id + ']'"
                :disabled="hasSuperAdminRole && role.id !== superAdminRoleId"
                inline
              />
            </li>
          </ul>
        </TabPanel>

        <TabPanel>
          <ul class="flex flex-col items-start">
            <li v-for="(tenant, index) in tenants" :key="index">
              <SwitchField
                v-model="form.tenantIds[tenant.id]"
                :label="tenant.name"
                :field-id="'tenantIds[' + tenant.id + ']'"
                :field-name="'tenantIds[' + tenant.id + ']'"
                :disabled="hasSuperAdminRole"
                inline
              />
            </li>
          </ul>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteUserAction v-if="user" :user="user" :callback="deleteCallback">
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteUserAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
